import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import useSWR from "swr";
import useGeneralHook from "../../../common/hook/useGeneralHook";

interface TableItem {
  indicatorName: string;
  value: string;
  cmax: number;
  cmin: number;
  status: number;
  indicatorUnit: string;
}

interface MonitoringTableProps {
  data: TableItem[];
  onLastPageReached?: () => void;
  autoAdvance?: boolean;
}

const MonitoringTable: React.FC<MonitoringTableProps> = ({
  data,
  onLastPageReached,
  autoAdvance = true,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const paramsPerView = 12;
  const totalPages = Math.ceil(data.length / paramsPerView);

  const { dispatch, API_PATHS, fetchThunk } = useGeneralHook();
  const { data: dataTime } = useSWR(API_PATHS.monitoring.timeSwitch, async (url: string) => {
    const json = await dispatch(fetchThunk(url));
    return json?.data;
  });

  useEffect(() => {
    if (!autoAdvance) return;

    const timeSwitch =
      dataTime?.timeSwitch && typeof dataTime.timeSwitch === "number"
        ? dataTime.timeSwitch
        : 30;

    const interval = setInterval(() => {
      const nextPage = (currentPage + 1) % totalPages;
      setCurrentPage(nextPage);

      if (nextPage === 0 && onLastPageReached) {
        onLastPageReached();
      }
    }, timeSwitch * 1000);

    return () => clearInterval(interval);
  }, [currentPage, totalPages, dataTime, onLastPageReached, autoAdvance]);

  const displayedParams = data.slice(
    currentPage * paramsPerView,
    (currentPage + 1) * paramsPerView
  );

  const formatCValue = (value: number | null) => {
    return value === null ? "Không có giới hạn" : value.toFixed(2);
  };

  return (
    <TableContainer component={Paper} sx={{ border: "1px solid white" }}>
      <Table sx={{ borderCollapse: "collapse", backgroundColor: "#068df3" }}>
        <TableHead>
          <TableRow>
            {["THÔNG SỐ", "GIÁ TRỊ", "QCVN"].map((header, i) => (
              <TableCell
                key={i}
                sx={{
                  border: "1px solid white",
                  fontWeight: "bold",
                  fontSize: "2rem",
                  textAlign: "center",
                  "@media (max-width: 800px)": { fontSize: "1rem" },
                }}
              >
                {header}
              </TableCell>
            ))}
            {displayedParams.length > 6 &&
              ["THÔNG SỐ", "GIÁ TRỊ", "QCVN"].map((header, i) => (
                <TableCell
                  key={`2nd-${i}`}
                  sx={{
                    border: "1px solid white",
                    fontWeight: "bold",
                    fontSize: "2rem",
                    textAlign: "center",
                    "@media (max-width: 800px)": { fontSize: "1rem" },
                  }}
                >
                  {header}
                </TableCell>
              ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedParams.slice(0, 6).map((row, index) => (
            <TableRow key={index} sx={{ height: "9vh", "@media (max-width: 800px)": { height: "6vh" } }}>
              {[
                `${row?.indicatorName} (${row?.indicatorUnit})`,
                row?.value,
                row?.cmin === null && row?.cmax === null
                  ? "Không có giới hạn"
                  : row?.cmin === null
                  ? `<= ${formatCValue(row.cmax)}`
                  : row?.cmax === null
                  ? `>= ${formatCValue(row.cmin)}`
                  : `${formatCValue(row.cmin)} - ${formatCValue(row.cmax)}`,
              ].map((value, i) => (
                <TableCell
                  key={i}
                  sx={{
                    border: "1px solid white",
                    fontSize: "1.5rem",
                    textAlign: "center",
                    fontWeight: "bold",
                    color: i === 1 && row.status === 80 ? "red" : "white",
                    "@media (max-width: 800px)": { fontSize: "1rem" },
                  }}
                >
                  {value}
                </TableCell>
              ))}
              {displayedParams[index + 6] && (
                <>
                  {[
                    `${displayedParams[index + 6].indicatorName} (${displayedParams[index + 6].indicatorUnit})`,
                    displayedParams[index + 6].value,
                    displayedParams[index + 6].cmin === null &&
                    displayedParams[index + 6].cmax === null
                      ? "Không có giới hạn"
                      : displayedParams[index + 6].cmin === null
                      ? `<= ${formatCValue(displayedParams[index + 6].cmax)}`
                      : displayedParams[index + 6].cmax === null
                      ? `>= ${formatCValue(displayedParams[index + 6].cmin)}`
                      : `${formatCValue(displayedParams[index + 6].cmin)} - ${formatCValue(displayedParams[index + 6].cmax)}`,
                  ].map((value, i) => (
                    <TableCell
                      key={`2nd-${i}`}
                      sx={{
                        border: "1px solid white",
                        fontSize: "1.5rem",
                        textAlign: "center",
                        fontWeight: "bold",
                        color:
                          i === 1 &&
                          displayedParams[index + 6]?.status === 80
                            ? "red"
                            : "white",
                        "@media (max-width: 800px)": { fontSize: "1rem" },
                      }}
                    >
                      {value}
                    </TableCell>
                  ))}
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MonitoringTable;
